import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, X } from 'lucide-react';
import { Button, Label, Select, Stack } from '@easymoving/ui';
import { useBackend } from '../../lib/backend/context';
import { useCustomRooms, useItems } from '../../hooks/useUserData';
import { roomOptions } from '../../lib/taxonomy/options';
import { captureItemNumber } from '../../lib/capture/autoName';

const NONE = '__none__';

interface Captured {
  id: string;
  previewUrl: string;
}

/**
 * Photo-first capture: the only required interaction is taking a photo. Each
 * shot immediately creates an auto-named item with `destination: 'undecided'`,
 * so everything lands in the /app/needs triage screen to be organized later.
 */
export function PhotoCaptureMode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const backend = useBackend();
  const { items, loading: itemsLoading } = useItems();
  const customRooms = useCustomRooms();

  const [room, setRoom] = useState(NONE); // sticky across captures
  const [captured, setCaptured] = useState<Captured[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // `items` is a live subscription, so snapshot the count at the first save —
  // otherwise numbering would double-count items added during this session.
  const baseCountRef = useRef<number | null>(null);
  const capturedRef = useRef<Captured[]>([]);
  // Ids the user has deliberately removed — the in-flight upload for one of
  // these must not surface an error toast, and its uploaded object (if any)
  // still needs cleanup once the upload lands.
  const removedRef = useRef<Set<string>>(new Set());

  capturedRef.current = captured;

  // Release the object URLs created for the session thumbnails.
  useEffect(() => {
    return () => {
      for (const c of capturedRef.current) URL.revokeObjectURL(c.previewUrl);
    };
  }, []);

  async function onFile(file: File | null) {
    if (!file || busy) return;
    // Clear the input's value immediately (not after the async work below) — a
    // file input never fires 'change' for re-selecting the same file unless its
    // value was reset first, so this has to happen before the next shot, not after.
    if (inputRef.current) inputRef.current.value = '';
    setBusy(true);
    setError(null);
    // Don't lock in the base count while the items subscription is still
    // loading — that would permanently stamp this session with a partial
    // (or zero) count. Fall back to the current (possibly incomplete) count
    // for this shot only; once loading finishes, a later shot snapshots it.
    if (baseCountRef.current === null && !itemsLoading) baseCountRef.current = items.length;
    const baseCount = baseCountRef.current ?? items.length;
    const n = captureItemNumber(baseCount, captured.length);
    const previewUrl = URL.createObjectURL(file);
    let id: string;
    try {
      id = await backend.createItem({
        name: t('capture.autoName', { n }),
        room: room === NONE ? undefined : room,
        destination: 'undecided',
        progressStatus: 'not_started',
      });
    } catch {
      URL.revokeObjectURL(previewUrl);
      setError(t('capture.createFailed'));
      setBusy(false);
      return;
    }
    setCaptured((prev) => [...prev, { id, previewUrl }]);
    // Release the shutter for the next shot now — the item already exists and
    // is visible, so the upload below can finish in the background without
    // blocking back-to-back captures.
    setBusy(false);
    try {
      await backend.uploadPrimaryImage(id, file);
      // The capture may have been removed while the upload was still in
      // flight — the item doc is gone, so the object that just landed is
      // orphaned. Clean it up rather than leaving it unreachable.
      if (removedRef.current.has(id)) void backend.deleteItemImages(id);
    } catch {
      // If the user already deleted this capture, the item doc is gone —
      // the "item was saved" error would be false, so stay silent and just
      // clean up whatever Storage object made it through.
      if (removedRef.current.has(id)) {
        void backend.deleteItemImages(id);
        return;
      }
      // The item is intentionally kept — a capture is never silently lost.
      setError(t('capture.uploadFailed'));
    }
  }

  async function remove(c: Captured) {
    setError(null);
    try {
      await backend.deleteItem(c.id);
    } catch {
      setError(t('capture.removeFailed'));
      return;
    }
    removedRef.current.add(c.id);
    void backend.deleteItemImages(c.id);
    setCaptured((prev) => prev.filter((x) => x.id !== c.id));
    URL.revokeObjectURL(c.previewUrl);
  }

  const roomOpts = [{ value: NONE, label: '—' }, ...roomOptions(t, customRooms)];

  return (
    <Stack gap="md" data-testid="photo-capture">
      <p className="text-graphite text-[14px]">{t('capture.shootHint')}</p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label={t('capture.shoot')}
        onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-rule-strong bg-surface-tint flex flex-col items-center justify-center gap-2 text-graphite hover:border-petrol hover:text-petrol transition-colors disabled:opacity-60"
      >
        <Camera className="size-10" />
        <span className="text-[16px] font-medium">{t('capture.shoot')}</span>
      </button>

      {captured.length > 0 && (
        <p className="text-center text-olive text-[14px]" role="status">
          {t('capture.added', { count: captured.length })}
        </p>
      )}

      {error && (
        <p className="text-center text-clay text-[14px]" role="alert">
          {error}
        </p>
      )}

      <Label>
        {t('capture.roomOptional')}
        <Select
          ariaLabel={t('capture.roomOptional')}
          value={room}
          options={roomOpts}
          onValueChange={setRoom}
        />
      </Label>

      {captured.length > 0 && (
        <div className="grid grid-cols-4 gap-2" data-testid="capture-strip">
          {captured.map((c) => (
            <div key={c.id} className="relative aspect-square rounded-lg overflow-hidden surface">
              <img src={c.previewUrl} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => void remove(c)}
                aria-label={t('capture.removeCapture')}
                className="absolute top-1 end-1 bg-ink/70 text-white rounded-full p-1"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {captured.length > 0 && (
        <Button size="lg" className="w-full" onClick={() => navigate('/app/needs')}>
          {t('capture.doneOrganize')}
        </Button>
      )}
    </Stack>
  );
}
